import {ReactNode, useEffect, useState} from 'react';
import styles from './OutcomesCarousel.module.css';

/** Paywall outcomes carousel — 5 auto-cycling panels.
 *
 * Mirrors Nyx-iOS PaywallOutcomesCarousel.tsx structure:
 *   Sleep care → Energy → Heart → Brain → Relationships
 *
 * Active pill expands and shows label + progress sweep; tap to jump.
 * Each panel auto-advances every CYCLE_MS (5500ms per Nyx). Tap on
 * active pill restarts the cycle.
 *
 * Per-panel vizes are simplified vs Nyx (which uses Reanimated shared
 * values + per-segment SVG morphs) — we keep the title, citation, and
 * a single representative animation per panel. */
const CYCLE_MS = 5500;

const SleepIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round">
    <path d="M11 8.5a4.5 4.5 0 0 1-5.5-5.5 4.5 4.5 0 1 0 5.5 5.5z" strokeLinejoin="round" />
  </svg>
);

const EnergyIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinejoin="round">
    <path d="M7 1 L3 8 L7 8 L5 13 L11 6 L7 6 Z" />
  </svg>
);

const HeartIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinejoin="round">
    <path d="M7 12 C 3 9 1.5 7 1.5 5 a 2.5 2.5 0 0 1 5-1 a 2.5 2.5 0 0 1 5 1 C 11 7 9 9 7 12 Z" />
  </svg>
);

const BrainIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinejoin="round">
    <path d="M5 3 L 3 3.5 L 2.5 5.5 L 3 7.5 L 2.5 9 L 3 11 L 5 11.5 L 6.5 11.5 L 6.5 3 Z" />
    <path d="M9 3 L 11 3.5 L 11.5 5.5 L 11 7.5 L 11.5 9 L 11 11 L 9 11.5 L 7.5 11.5 L 7.5 3 Z" />
  </svg>
);

const RelationshipsIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.3}>
    <circle cx={4.5} cy={4.5} r={1.6} />
    <circle cx={9.5} cy={4.5} r={1.6} />
    <path d="M1.5 11.5 L 1.5 10 L 3 8.5 L 4.5 8.5 L 6 10 L 6 11.5 M 6.5 11.5 L 6.5 10 L 8 8.5 L 9.5 8.5 L 11 10 L 11 11.5" />
  </svg>
);

type PillDef = {
  key: string;
  label: string;
  Icon: React.FC;
};

const PILLS: PillDef[] = [
  {key: 'sleep', label: 'Sleep care', Icon: SleepIcon},
  {key: 'energy', label: 'Energy', Icon: EnergyIcon},
  {key: 'heart', label: 'Heart', Icon: HeartIcon},
  {key: 'brain', label: 'Brain', Icon: BrainIcon},
  {key: 'relationships', label: 'Relationships', Icon: RelationshipsIcon},
];

const Gold = ({children}: {children: ReactNode}) => <span className="osa-paywall-gold">{children}</span>;

/** Single Sleep panel — bar chart of breathing interruptions / hr.
 *  9 bars: first 5 stay pink (untreated), last 4 fade to gold (treated). */
const SleepPanel = () => (
  <>
    <h4 className={styles.panelTitle}>
      <Gold>More sleep</Gold>, with 26% fewer interruptions
    </h4>
    <div className={styles.sleepBars}>
      {[60, 70, 90, 70, 80, 50, 35, 25, 20].map((h, i) => (
        <span
          key={i}
          className={`${styles.sleepBar} ${i >= 5 ? styles.sleepBarGold : ''}`}
          style={{height: `${h}%`, animationDelay: `${i * 60}ms`}}
        />
      ))}
    </div>
    <div className={styles.vizCompare}>
      <div className={styles.vcSide}>
        <span className={styles.vcWhen}>Today</span>
        <span className={styles.vcDetail}>≥30/hr</span>
      </div>
      <span className={styles.vcArrow}>→</span>
      <div className={`${styles.vcSide} ${styles.vcAfter}`}>
        <span className={styles.vcWhen}>10% weight loss</span>
        <span className={styles.vcDetail}>−22/hr</span>
      </div>
    </div>
    <p className={styles.panelCite}>AASM: severe ≥30/hr · Peppard et al. · JAMA 2000</p>
  </>
);

/** Energy panel — descending polyline (less daytime sleepiness). */
const EnergyPanel = () => (
  <>
    <h4 className={styles.panelTitle}>
      Daytime sleepiness, cut in <Gold>half</Gold>
    </h4>
    <div className={styles.energySvgWrap}>
      <svg viewBox="0 0 320 84" preserveAspectRatio="none" className={styles.energySvg}>
        <line x1="0" y1="68" x2="320" y2="68" stroke="rgba(255,255,255,0.15)" strokeDasharray="2 4" />
        <polyline
          className={styles.energyTrail}
          points="20,18 90,22 165,40 235,56 300,66"
          pathLength={100}
          fill="none"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle className={styles.energyDot} cx="20" cy="18" r="5.5" />
      </svg>
    </div>
    <div className={styles.vizCompare}>
      <div className={styles.vcSide}>
        <span className={styles.vcWhen}>Tonight</span>
        <span className={styles.vcDetail}>56% report daytime sleepiness</span>
      </div>
      <span className={styles.vcArrow}>→</span>
      <div className={`${styles.vcSide} ${styles.vcAfter}`}>
        <span className={styles.vcWhen}>With treatment</span>
        <span className={styles.vcDetail}>28%</span>
      </div>
    </div>
    <p className={styles.panelCite}>PMC8381644 · Daytime sleepiness reduction</p>
  </>
);

/** Heart panel — draining donut ring + center stat. */
const HeartPanel = () => (
  <>
    <h4 className={styles.panelTitle}>
      <Gold>64%</Gold> lower long-term heart risk
    </h4>
    <div className={styles.heartRingWrap}>
      <svg viewBox="0 0 100 100" className={styles.heartRing}>
        <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(12,10,24,0.08)" strokeWidth="6" />
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="#382365"
          strokeWidth="6"
          strokeDasharray="100"
          strokeDashoffset="36"
          pathLength={100}
          className={styles.heartRingFill}
          transform="rotate(-90 50 50)"
        />
      </svg>
      <div className={styles.heartCenter}>
        <span className={styles.heartStat}>−64%</span>
        <span className={styles.heartLabel}>heart risk</span>
      </div>
    </div>
    <div className={styles.heartLegend}>
      <span className={styles.heartLegendItem}>
        <span className={styles.heartLegendDot} />
        Untreated
      </span>
      <span className={styles.heartLegendItem}>
        <span className={`${styles.heartLegendDot} ${styles.heartLegendDotGold}`} />
        With treatment
      </span>
    </div>
    <p className={styles.panelCite}>AHA Scientific Statement · 2021</p>
  </>
);

/** Brain panel — 3 cognitive measures, lighting up sequentially. */
const BrainPanel = () => (
  <>
    <h4 className={styles.panelTitle}>
      Sharper across <Gold>3 cognitive measures</Gold>
    </h4>
    <div className={styles.brainRows}>
      {['Processing speed', 'Visual search', 'Anxiety'].map((label, i) => (
        <div key={label} className={styles.brainRow} style={{animationDelay: `${600 + i * 350}ms`}}>
          <span className={styles.brainCheck}>
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M2.5 6 L 5 8.5 L 9.5 3.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span>{label}</span>
        </div>
      ))}
    </div>
    <p className={styles.panelCite}>Meta-analysis of 17 RCTs · AASM</p>
  </>
);

/** Relationships panel — two overlaid waveforms (loud tonight → quiet month 2). */
const RelationshipsPanel = () => (
  <>
    <h4 className={styles.panelTitle}>
      Snoring often fades by <Gold>month 2</Gold>
    </h4>
    <div className={styles.relSvgWrap}>
      <svg viewBox="0 0 320 84" preserveAspectRatio="none" className={styles.relSvg}>
        {/* Loud "Tonight" trace — large amplitude */}
        <path
          className={styles.relWaveTonight}
          d="M 10 42 Q 30 14, 50 42 T 90 42 T 130 42 T 170 42 T 210 42 T 250 42 T 290 42 T 310 42"
          fill="none"
          strokeWidth="2.2"
          strokeLinecap="round"
          pathLength={100}
        />
        {/* Quiet "Month 2" trace — small amplitude */}
        <path
          className={styles.relWaveMonth}
          d="M 10 42 Q 25 34, 40 42 T 70 42 T 100 42 T 130 42 T 160 42 T 190 42 T 220 42 T 250 42 T 290 42 T 310 42"
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          pathLength={100}
        />
      </svg>
    </div>
    <div className={styles.vizCompare}>
      <div className={styles.vcSide}>
        <span className={styles.vcWhen}>Tonight</span>
        <span className={styles.vcDetail}>loud snoring</span>
      </div>
      <span className={styles.vcArrow}>→</span>
      <div className={`${styles.vcSide} ${styles.vcAfter}`}>
        <span className={styles.vcWhen}>Month 2</span>
        <span className={styles.vcDetail}>quieter</span>
      </div>
    </div>
    <p className={styles.panelCite}>Braver et al. · CHEST 1995</p>
  </>
);

const PANEL_RENDERERS: Record<number, React.FC> = {
  0: SleepPanel,
  1: EnergyPanel,
  2: HeartPanel,
  3: BrainPanel,
  4: RelationshipsPanel,
};

export const OutcomesChart = () => {
  const [active, setActive] = useState(0);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => {
      setActive(a => (a + 1) % PILLS.length);
      setTick(t => t + 1);
    }, CYCLE_MS);
    return () => clearTimeout(id);
  }, [active, tick]);

  const handlePillClick = (i: number) => {
    if (i === active) {
      setTick(t => t + 1);
      return;
    }
    setActive(i);
    setTick(t => t + 1);
  };

  const Panel = PANEL_RENDERERS[active];

  return (
    <div className={styles.root}>
      <div className={styles.pillRow}>
        {PILLS.map((pill, i) => {
          const isActive = i === active;
          return (
            <button
              key={pill.key}
              type="button"
              className={`${styles.pill} ${isActive ? styles.pillActive : ''}`}
              onClick={() => handlePillClick(i)}
              aria-pressed={isActive}
            >
              {isActive ? (
                <span
                  key={`progress-${tick}`}
                  className={styles.pillProgress}
                  style={{animationDuration: `${CYCLE_MS}ms`}}
                />
              ) : null}
              <span className={styles.pillIcon}>
                <pill.Icon />
              </span>
              {isActive ? (
                <span key={`label-${tick}`} className={styles.pillLabel}>
                  {pill.label}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className={styles.panel}>
        <div key={`${active}-${tick}`} className={styles.panelInner}>
          <Panel />
        </div>
        <div className={styles.playDot} aria-hidden="true">
          <span className={styles.playDotBar} />
          <span className={styles.playDotBar} style={{animationDelay: '180ms'}} />
          <span className={styles.playDotBar} style={{animationDelay: '360ms'}} />
        </div>
      </div>
    </div>
  );
};

export default OutcomesChart;
